import { createContext, useContext, useState, useRef, ReactNode } from 'react';
import { ConfirmationDialog, ConfirmationOptions } from './dialog';

const ConfirmationServiceContext = createContext<
    (options: ConfirmationOptions<unknown>) => Promise<unknown>
>(Promise.reject);

export const useConfirmation = <T,>() => {
    return useContext(ConfirmationServiceContext) as (
        options: ConfirmationOptions<T>
    ) => Promise<T>;
};

interface ConfirmationServiceProviderProps {
    children?: ReactNode;
}

export const ConfirmationServiceProvider = ({ children }: ConfirmationServiceProviderProps) => {
    const [confirmationState, setConfirmationState] = useState<ConfirmationOptions<unknown> | null>(
        null
    );

    const awaitingPromiseRef = useRef<{
        resolve: (value?: unknown) => void;
        reject: () => void;
    }>();

    const openConfirmation = <T,>(options: ConfirmationOptions<T>) => {
        setConfirmationState(options);
        return new Promise<void>((resolve, reject) => {
            awaitingPromiseRef.current = { resolve, reject };
        });
    };

    const handleClose = () => {
        if (confirmationState.catchOnCancel && awaitingPromiseRef.current) {
            awaitingPromiseRef.current.reject();
        }

        setConfirmationState(null);
    };

    const handleSubmit = (value?: unknown) => {
        if (awaitingPromiseRef.current) {
            awaitingPromiseRef.current.resolve(value);
        }

        setConfirmationState(null);
    };

    return (
        <>
            <ConfirmationServiceContext.Provider value={openConfirmation}>
                {children}
            </ConfirmationServiceContext.Provider>

            <ConfirmationDialog
                open={Boolean(confirmationState)}
                onSubmit={handleSubmit}
                onClose={handleClose}
                {...confirmationState}
            />
        </>
    );
};
